import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { LoadingButton } from '@mui/lab'
import { Box, Button, Chip, Paper, Typography } from '@mui/material'
import { useCancelApplication } from '@/api/financial/application/cancel-application'
import { FeedbackDialog } from '@/components/feedback-dialog'
import { formatCurrency, toast } from '@/helpers'
import { routes } from '@/routes'
import { CustomerApplicationItem } from '@/types/customer-context'
import { creditProducts } from './products/credit-products'
import { creditProductLabel } from './credit-labels'

const statusChip: Record<
  CustomerApplicationItem['status'],
  { label: string; color: 'warning' | 'success' | 'info' | 'error' | 'default' }
> = {
  incomplete: { label: 'Incompleta', color: 'warning' },
  analysing: { label: 'Em análise', color: 'warning' },
  review: { label: 'Aguardando documento', color: 'warning' },
  approved: { label: 'Aprovado', color: 'success' },
  done: { label: 'Concluído', color: 'success' },
  denied: { label: 'Negado', color: 'error' },
  canceled: { label: 'Cancelado', color: 'default' },
}

const cancelableStatuses: CustomerApplicationItem['status'][] = [
  'incomplete',
  'review',
  'approved',
]

function nextAction(application: CustomerApplicationItem) {
  if (application.status === 'analysing') {
    return 'Estamos analisando. Você recebe o resultado por e-mail.'
  }
  if (application.status === 'review') {
    return 'Há uma pendência para corrigir.'
  }
  if (application.status === 'approved') {
    return 'Proposta disponível para você avaliar.'
  }
  const firstStep = creditProducts[application.product].steps[0]
  return `Próxima etapa: ${firstStep}`
}

function openedOn(application: CustomerApplicationItem) {
  const date = new Date(application.created_at).toLocaleDateString('pt-BR')
  const amount = application.amount
    ? formatCurrency(Number(application.amount))
    : null
  return amount ? `Aberta em ${date} · ${amount}` : `Aberta em ${date}`
}

export function ApplicationCard({
  application,
}: {
  application: CustomerApplicationItem
}) {
  const navigate = useNavigate()
  const [confirming, setConfirming] = useState(false)
  const cancel = useCancelApplication({
    onSuccess: () => {
      setConfirming(false)
      toast.success('Solicitação cancelada.')
    },
  })

  const chip = statusChip[application.status]
  const canCancel = cancelableStatuses.includes(application.status)
  const canContinue = application.status !== 'analysing'

  return (
    <Paper variant="outlined" sx={{ p: 1.75, display: 'grid', gap: 1.5 }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: 1.5,
        }}
      >
        <Box>
          <Typography fontWeight={500}>
            {creditProductLabel(application.product)}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {openedOn(application)}
          </Typography>
        </Box>
        <Chip label={chip.label} color={chip.color} size="small" />
      </Box>

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 1,
          flexWrap: 'wrap',
        }}
      >
        <Typography variant="body2" color="text.secondary">
          {nextAction(application)}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          {canCancel && (
            <Button
              variant="text"
              size="small"
              onClick={() => setConfirming(true)}
            >
              Cancelar solicitação
            </Button>
          )}
          {canContinue && (
            <Button
              size="small"
              onClick={() =>
                navigate(
                  routes.credit.application.replace(
                    ':application_key',
                    application.application_key,
                  ),
                )
              }
            >
              Continuar
            </Button>
          )}
        </Box>
      </Box>

      <FeedbackDialog
        open={confirming}
        type="error"
        title="Cancelar solicitação"
        closeLabel="Voltar"
        onClose={() => setConfirming(false)}
      >
        <Typography>
          A solicitação de {creditProductLabel(application.product)} vai para
          Encerradas e você pode começar outra.
        </Typography>
        <LoadingButton
          fullWidth
          color="error"
          loading={cancel.isPending}
          onClick={() => cancel.mutate(application.application_key)}
          sx={{ mt: 2 }}
        >
          Cancelar solicitação
        </LoadingButton>
      </FeedbackDialog>
    </Paper>
  )
}
